import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Eye, Link2 } from 'lucide-react'
import { Filter } from '../../../../../components/SubHeaderFilter/types'

import {
  TrackingContainer,
  StatusText,
  MessageContainer,
} from './style'

import ApiService from '../../../../../services/ApiService'

import ModalSteelOrder from '../ModalSteelOrder/modalsteel'

import { SteelOrder } from '../../SteelOrdersTable/types'
import UserContext from '../../../../../contexts/UserContext'
import { FeedbackContext } from '../../../../../contexts/FeedbackContext'
import {
  StandardDataTable,
  useColumnsState,
  StandardColumn,
} from '../../../../../components/StandardPage'

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`
const PAGE_SIZE = 50
const trackingScreenKey = 'steel_tracking'

type SteelOutletContext = {
  refresh: boolean
  appliedFilters: Filter[]
  search: string
}

const formatDate = (value?: string | Date) => {
  if (!value) return 'N/A'
  const date = new Date(value)
  if (isNaN(date.getTime())) return 'N/A'
  return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' })
}

const formatCurrency = (value?: number | string) => {
  if (value === undefined || value === null) return 'N/A'
  const num = Number(value)
  return isNaN(num)
    ? 'N/A'
    : num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const calculateStatus = (items: any[] | undefined) => {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return 'Aguardando'
  }

  const allFinished = items.every((i) => Number(i.status) === 2)
  if (allFinished) return 'Concluído'

  const hasInProgress = items.some(
    (i) => Number(i.status) === 1 || Number(i.status) === 2,
  )
  if (hasInProgress) return 'Em produção'

  return 'Aguardando'
}

const Tracking: React.FC = () => {
  const { refresh, appliedFilters, search } = useOutletContext<SteelOutletContext>()

  const [orders, setOrders] = useState<SteelOrder[]>([])
  const { user } = useContext(UserContext)
  const { setFeedback } = useContext(FeedbackContext)

  const userRole = (user?.role as string)?.toUpperCase()
  const isTenantClient = userRole === 'TENANT_CLIENT'
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [hasMore, setHasMore] = useState(false)

  const [selectedOrder, setSelectedOrder] = useState<SteelOrder | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [debouncedSearch, setDebouncedSearch] = useState(search)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)
    return () => clearTimeout(handler)
  }, [search])

  const fetchOrders = useCallback(
    async (pageNum: number, isRefresh: boolean = false) => {
      setIsLoading(true)
      try {
        const params: any = {
          page: pageNum,
          limit: PAGE_SIZE,
          tracking: true,
        }

        if (debouncedSearch && debouncedSearch.trim() !== '') {
          params.search = debouncedSearch
        }

        if (appliedFilters && appliedFilters.length > 0) {
          params.filters = encodeURIComponent(JSON.stringify(appliedFilters))
        }

        const response = await ApiService.get(
          `${API_BASE_URL}/order/paginated`,
          params,
        )

        const data = response.data?.data || []
        const total = response.data?.total || 0

        setOrders((prev) => (isRefresh ? data : [...prev, ...data]))
        setTotalItems(total)
        setHasMore(data.length === PAGE_SIZE && pageNum * PAGE_SIZE < total)
      } catch (error) {
        console.error('Erro ao buscar pedidos:', error)
      } finally {
        setIsLoading(false)
      }
    },
    [appliedFilters, debouncedSearch],
  )

  useEffect(() => {
    setPage(1)
    fetchOrders(1, true)
  }, [refresh, fetchOrders, debouncedSearch, appliedFilters])

  const handleLoadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    fetchOrders(nextPage, false)
  }

  const handleOpenModal = (order: SteelOrder) => {
    setSelectedOrder(order)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedOrder(null)
  }

  const handleGenerateLink = async (order: SteelOrder) => {
    try {
      const payload = {
        companyId: (order as any).clientId,
        cnpj: (order as any).clientCnpj || '',
        name: (order as any).client || 'Cliente',
        email: (order as any).clientEmail || '',
        company: (order as any).client || '',
        orders: [order.id],
        approved: 'approved',
        adminUser: false,
        role: 'TENANT_CLIENT',
        permissions: ['order'],
      }

      const response = await ApiService.user.generateLink(payload)

      if (response.data && response.data.link) {
        await navigator.clipboard.writeText(response.data.link)
        setFeedback({
          isOpen: true,
          message: 'Link gerado e copiado para a área de transferência!',
          statusOk: true,
        })
      }
    } catch (error) {
      setFeedback({
        isOpen: true,
        message: 'Erro ao gerar o link. Tente novamente.',
        statusOk: false,
      })
    }
  }

  const columns = useMemo<StandardColumn<SteelOrder>[]>(() => {
    const cols: StandardColumn<SteelOrder>[] = []

    if (!isTenantClient) {
      cols.push({
        id: 'saleNumberPloomes',
        label: 'Nº do pedido',
        accessor: (order) => order.saleNumberPloomes ?? '',
      })
    }

    cols.push(
      {
        id: 'npc',
        label: 'NPC',
        accessor: (order) => order.npc ?? '',
      },
      {
        id: 'client',
        label: 'Pedido cliente',
        accessor: (order) => (order as any).client ?? '--',
      },
      {
        id: 'total',
        label: 'Valor (R$)',
        accessor: (order) => Number((order as any).total ?? 0),
        cell: (order) => <>{formatCurrency((order as any).total)}</>,
        exportValue: (order) => formatCurrency((order as any).total),
      },
      {
        id: 'orderDate',
        label: 'Data pedido',
        accessor: (order) => (order as any).orderDate ?? '',
        cell: (order) => <>{formatDate((order as any).orderDate)}</>,
        exportValue: (order) => formatDate((order as any).orderDate),
      },
      {
        id: 'deliveryDate',
        label: 'Previsão entrega',
        accessor: (order) => (order as any).deliveryDate ?? '',
        cell: (order) => <>{formatDate((order as any).deliveryDate)}</>,
        exportValue: (order) => formatDate((order as any).deliveryDate),
      },
      {
        id: 'status',
        label: 'Status',
        accessor: (order) => calculateStatus((order as any).items),
        cell: (order) => (
          <StatusText>{calculateStatus((order as any).items)}</StatusText>
        ),
      },
      {
        id: 'itemsCount',
        label: 'Qtd. de itens',
        accessor: (order) => (order as any).items?.length || 0,
      },
    )

    return cols
  }, [isTenantClient])

  const { columnsState, handleColumnsChange, handleRestoreColumns } =
    useColumnsState(trackingScreenKey, columns)

  if (isLoading && orders.length === 0) {
    return (
      <TrackingContainer>
        <MessageContainer>Carregando pedidos...</MessageContainer>
      </TrackingContainer>
    )
  }

  return (
    <TrackingContainer>
      <StandardDataTable
        columns={columns}
        data={orders}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={totalItems}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        isLoading={isLoading}
        countLabel='pedidos'
        exportFileName='rastreamento-pedidos'
        emptyMessage='Nenhum pedido encontrado.'
        rowActions={(order) => [
          { label: 'Ver Detalhes', icon: <Eye size={16} />, onClick: () => handleOpenModal(order) },
          ...(!isTenantClient
            ? [{ label: 'Gerar link', icon: <Link2 size={16} />, onClick: () => handleGenerateLink(order) }]
            : []),
        ]}
      />

      <ModalSteelOrder
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        order={selectedOrder}
      />
    </TrackingContainer>
  )
}

export default Tracking
